'use client';

import cx from 'classix';
import { useSearchParams } from 'next/navigation';
import {
  FaChevronLeft,
  FaChevronRight,
} from 'react-icons/fa6';

import { type ListingsCollection } from '@/features/listing/types/listing-types';
import {
  Link,
  usePathname,
} from '@/i18n/routing';

import styles from './pagination.module.scss';

interface PaginationProps {
  itemsCount: ListingsCollection['count'];
  itemsPerPage: number;
  pageNumberParamName: string;
}

export default function Pagination(props: PaginationProps) {
  const {
    itemsCount,
    itemsPerPage,
    pageNumberParamName,
  } = props;

  const searchParams = useSearchParams();
  const pathname = usePathname();
  const params = new URLSearchParams(searchParams);

  if (params.has('r')) {
    params.delete('r');
    params.delete('t');
  }

  const currentPage = params.get(pageNumberParamName) ? Number(params.get(pageNumberParamName)) : 1;
  const totalPages = Math.ceil(itemsCount / itemsPerPage);
  const isFirstPage = currentPage === 1;
  const isLastPage = currentPage === totalPages;

  let nextPageLink = '';
  let previousPageLink = '';

  if (!isLastPage) {
    params.set(pageNumberParamName, String(currentPage + 1));
    nextPageLink = `${pathname}?${params.toString()}`;
  }

  if (!isFirstPage) {
    params.set(pageNumberParamName, String(currentPage - 1));
    previousPageLink = `${pathname}?${params.toString()}`;
  }

  const renderPageNumbers = () => {
    let pageNumbers: (number | string)[] = [];
    const pageLimit = 5; // Adjust this value as needed

    if (totalPages <= pageLimit) {
      pageNumbers = Array.from({ length: totalPages }, (_, index) => index + 1);
    } else {
      const startPage = Math.max(1, currentPage - 1);
      const endPage = Math.min(totalPages, currentPage + 1);

      pageNumbers.push(1);

      if (startPage > 2) {
        pageNumbers.push('previousEllipsis');
      }

      // eslint-disable-next-line no-plusplus
      for (let index = startPage; index <= endPage; index++) {
        pageNumbers.push(index);
      }

      if (endPage < totalPages - 1) {
        pageNumbers.push('nextEllipsisPrev');
      }

      pageNumbers.push(totalPages);
    }

    // Remove duplicates and sort the page numbers
    const uniquePageNumbers = [...new Set(pageNumbers)];

    return uniquePageNumbers.map((page) => {
      if (typeof page === 'number') {
        params.set(pageNumberParamName, String(page));
        const paginationLink = `${pathname}?${params.toString()}`;
        const isActive = page === currentPage;

        return (
          <li key={page}>
            {isActive ? (
              <p
                aria-disabled
                aria-current="page"
                className={cx(styles.listingsPagination_item, styles.listingsPagination_item__isActive)}
                aria-label={`Page ${String(page)}, Current Page`}
              >
                <span className={styles.listingsPagination_hidden}>
                  page
                  {' '}
                </span>

                {page}

                {(page === totalPages || page === 1) && (
                  <span className={styles.listingsPagination_hidden}>
                    {' '}
                    {page === totalPages ? 'Last page' : 'First page'}
                  </span>
                )}
              </p>
            ) : (
              // eslint-disable-next-line canonical/prefer-react-lazy
              <Link
                className={styles.listingsPagination_item}
                href={paginationLink}
                aria-label={`Page ${String(page)}`}
              >
                <span className={styles.listingsPagination_hidden}>
                  page
                  {' '}
                </span>

                {page}

                {(page === totalPages || page === 1) && (
                  <span className={styles.listingsPagination_hidden}>
                    {' '}
                    {page === totalPages ? 'Last page' : 'First page'}
                  </span>
                )}
              </Link>
            )}
          </li>
        );
      }

      return (
        <li key={page} className={cx(styles.listingsPagination_itemEllipsis)}>
          <span>&#8230;</span>
        </li>
      );
    });
  };

  return (
    <nav className={styles.listingsPagination} aria-label="pagination">
      <ul className={styles.listingsPagination_list}>
        <li>
          {isFirstPage ? (
            <p aria-disabled className={cx(styles.listingsPagination_item, styles.listingsPagination_item__isDisabled)}>
              {/* eslint-disable-next-line canonical/prefer-react-lazy */}
              <FaChevronLeft />
              <span className={styles.listingsPagination_hidden}>Previous page</span>
            </p>
          ) : (
            // eslint-disable-next-line canonical/prefer-react-lazy
            <Link className={styles.listingsPagination_item} href={previousPageLink}>
              {/* eslint-disable-next-line canonical/prefer-react-lazy */}
              <FaChevronLeft />
              <span className={styles.listingsPagination_hidden}>Previous page</span>
            </Link>
          )}
        </li>

        {renderPageNumbers()}

        <li>
          {isLastPage ? (
            <p aria-disabled className={cx(styles.listingsPagination_item, styles.listingsPagination_item__isDisabled)}>
              {/* eslint-disable-next-line canonical/prefer-react-lazy */}
              <FaChevronRight />
              <span className={styles.listingsPagination_hidden}>Next page</span>
            </p>
          ) : (
            // eslint-disable-next-line canonical/prefer-react-lazy
            <Link className={styles.listingsPagination_item} href={nextPageLink}>
              {/* eslint-disable-next-line canonical/prefer-react-lazy */}
              <FaChevronRight />
              <span className={styles.listingsPagination_hidden}>Next page</span>
            </Link>
          )}
        </li>
      </ul>
    </nav>
  );
}
